import {
    Box,
    FormControlLabel,
    MenuItem,
    Radio,
    Stack,
    Grid,
} from '@mui/material';
import { common } from '@mui/material/colors';
import { BaseCheckbox } from 'common/components/base-checkbox-group';
import BaseNumberField from 'common/components/base-number-field';
import { BaseSelectField } from 'common/components/base-select-field';
import BaseTextField from 'common/components/base-text-field';
import BaseImageUpload from 'common/components/base-upload-img-question';
import RequiredLabel from 'common/components/required-label';
import { styleInput, styleInputNumber } from 'common/constants';
import React, { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { createStyleSheet, handleError } from 'utils/helpers';
import { IconButton } from '@mui/material';
import { FieldErrors } from 'react-hook-form';
import CampaignConstants from 'pages/admin/campaign/constants';
import {
    IQuestionAnswer,
    IQuestionBaseProps,
    IQuestionNewEditForm,
} from 'types/campaign-design';
import QuestionDnD from './question-dnd';
import CloseIcon from '@mui/icons-material/Close';
import MinusIcon from '@mui/icons-material/Remove';
import PlusIcon from '@mui/icons-material/Add';
import ModalConfirm from 'common/components/modal-confirm';
import { useParams } from 'react-router-dom';
import { deleteAnswerOptions } from 'services/campaign.service';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import { removeSkipLogicFromQuestionAffected } from 'slices/campaignsSlice';

const questionAnswerType = CampaignConstants.questionAnswerType;

const QuestionSingleChoice: React.FC<IQuestionBaseProps> = ({
    question,
    ...props
}) => {
    const currentCampaign = useAppSelector(
        (state) => state.campaignState.campaign
    );
    const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
    const {
        watch,
        control,
        setValue,
        getValues,
        formState,
        setError,
        clearErrors,
    } = useFormContext();
    const { append, remove, insert } = useFieldArray({
        control,
        name: `data.answers`,
    });
    const numberOfRows = watch(`data.numberOfRows`);
    const addAnotherAnswer = watch(`data.addAnotherAnswer`);
    const insertMultipleAnswers = watch(`data.insertMultipleAnswers`);
    const multipleAnswers = watch(`data.multipleAnswers`);
    const anotherAnswerText = watch(`data.anotherAnswerText`);
    const answers = watch('data.answers');
    const countNormalAnswers =
        answers?.filter(
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            (item: any) => item.type === questionAnswerType.NORMAL_ANSWER
        ).length || 0;

    useEffect(() => {
        if (!answers) {
            return;
        }
        answers
            .sort(
                (
                    a: any, // eslint-disable-line @typescript-eslint/no-explicit-any
                    b: any // eslint-disable-line @typescript-eslint/no-explicit-any
                ) => a?.type?.localeCompare(b?.type)
            )
            .forEach((field: IQuestionAnswer, fieldIndex: number) => {
                setValue(`data.answers.${fieldIndex}`, field);
            });
        // eslint-disable-next-line
    }, [answers]);

    const handleSave = (fieldIndex: number) => {
        const value = getValues(`data.answers.${fieldIndex}.answer`);
        if (!value) {
            setError(`data.answers.${fieldIndex}.answer`, {
                type: 'manual',
                message: 'Vui lòng nhập',
            });
        } else {
            clearErrors(`data.answers.${fieldIndex}.answer`);
        }
        setValue(`data.answers.${fieldIndex}.answer`, value);
        setEditingIndex(null);
    };

    const handleTextareaBlur = () => {
        const lines = multipleAnswers
            ?.split('\n')
            ?.filter((line: string) => line.trim() !== '');
        lines?.forEach((line: string) =>
            append({
                answer: line,
                image: null,
                type: questionAnswerType.NORMAL_ANSWER,
            })
        );
        setValue('data.numberOfRows', countNormalAnswers + lines.length);
        setValue('data.multipleAnswers', '');
        setValue('data.insertMultipleAnswers', false);
    };

    const errors = formState?.errors?.data as FieldErrors<{
        answers: { answer: string; image: File }[];
    }>;
    const handleAddNoAnswerYet = (checked: boolean) => {
        if (checked) {
            append({
                answer: 'Không có đáp án nào trên đây',
                image: null,
                type: questionAnswerType.NO_ANSWER,
            });
        } else {
            const index = answers.findIndex(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (field: any) => field.type === questionAnswerType.NO_ANSWER
            );
            if (index !== -1) {
                remove(index);
            }
        }
    };
    const handleAddAnotherAnswer = (checked: boolean) => {
        if (checked) {
            append({
                answer: 'Đáp án khác (vui lòng ghi rõ)',
                image: null,
                type: questionAnswerType.OTHER_ANSWER,
            });
        } else {
            const index = answers.findIndex(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (field: any) => field.type === questionAnswerType.OTHER_ANSWER
            );
            if (index !== -1) {
                remove(index);
            }
        }
    };

    const removeAnswerImage = (fieldIndex: number) => {
        setValue(`data.answers.${fieldIndex}.image`, null, {
            shouldDirty: true,
        });
    };

    const [openModalDeleteAnswer, setOpenModalDeleteAnswer] =
        React.useState(false);
    const [modalDeleteAnswerText, setModalDeleteAnswerText] =
        React.useState<string>('');
    const [modalDeleteAnswerIndex, setModalDeleteAnswerIndex] =
        React.useState<number>(0);

    const campaignId = useParams<{ id: string }>().id;
    const questions = useAppSelector(
        (state: RootState) => state.campaignState.campaignQuestions
    );
    const [rootListQuestionsAffected, setRootListQuestionsAffected] =
        React.useState<
            {
                questionNumber: number | null | undefined;
                question: string;
                questionId?: number | null | string;
                skipId?: number | null | string;
            }[]
        >([]);
    const checkRemoveAnswer = (fieldIndex: number) => {
        const listQuestionsAffected: {
            questionNumber: number | null | undefined;
            question: string;
            questionId?: number | null | string;
            skipId?: number | null | string;
        }[] = [];
        if (question.creeningLogic && question.creeningLogic.length > 0) {
            const findAnswer = question.creeningLogic.find(
                (item) => item.answer_id === answers[fieldIndex].id
            );
            if (findAnswer) {
                listQuestionsAffected.push({
                    questionNumber: question.questionNumber,
                    question: question.question,
                });
            }
        }
        questions.forEach((item: IQuestionNewEditForm) => {
            if (item.skipLogic && item.skipLogic.length > 0) {
                const findLogic = item.skipLogic.find(
                    (item) => item.answer_id === answers[fieldIndex].id
                );
                if (findLogic) {
                    listQuestionsAffected.push({
                        questionNumber: item.questionNumber,
                        question: item.question,
                        questionId: item.id,
                        skipId: findLogic.id,
                    });
                }
            }
        });
        if (listQuestionsAffected.length > 0) {
            const messageQuestion = listQuestionsAffected
                .map((item) => `Q${item.questionNumber}. ${item.question}`)
                .join(', ');
            setModalDeleteAnswerIndex(fieldIndex);
            setModalDeleteAnswerText(
                `Xoá đáp án này sẽ ảnh hưởng đến logic ở câu hỏi: ${messageQuestion}. Bạn có chắc chắn muốn xoá không?`
            );
            setRootListQuestionsAffected(listQuestionsAffected);
            setOpenModalDeleteAnswer(true);
        } else {
            removeAnswer(fieldIndex);
        }
    };
    const dispatch = useAppDispatch();
    const removeAnswer = async (fieldIndex: number) => {
        remove(fieldIndex);
        setValue('data.numberOfRows', numberOfRows ? numberOfRows - 1 : 0);
        setValue('data.numberOfRows', numberOfRows ? numberOfRows - 1 : 0);
        if (!question.id) {
            return;
        }
        try {
            const res = await deleteAnswerOptions(
                Number(campaignId),
                Number(question.id),
                answers[fieldIndex].id
            );
            if (res.success) {
                props.replaceQuestionByQuestionId(
                    Number(question.id),
                    listCampaignQuestionsFormatter(res.data.question)
                );
                dispatch(
                    removeSkipLogicFromQuestionAffected({
                        listAffected: rootListQuestionsAffected.map((item) => ({
                            questionId: item.questionId,
                            skipId: item.skipId,
                        })),
                    })
                );
                setRootListQuestionsAffected([]);
                toast.success(res.message);
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            handleError(error);
        }
    };

    const insertAnswer = (fieldIndex: number) => {
        insert(fieldIndex + 1, {
            id: null,
            answer: `Tuỳ chọn ${countNormalAnswers + 1}`,
            image: null,
            type: questionAnswerType.NORMAL_ANSWER,
        });
        setValue('data.numberOfRows', numberOfRows + 1);
    };

    const focusOnField = (fieldIndex: number) => {
        setEditingIndex(fieldIndex);
        setTimeout(() => {
            const field = document.querySelector(
                `input[name="data.answers.${fieldIndex}.answer"]`
            );
            if (field) {
                (field as HTMLInputElement).focus();
            }
        }, 100);
    };

    useEffect(() => {
        const text = anotherAnswerText || 'Đáp án khác (vui lòng ghi rõ)';

        if (addAnotherAnswer) {
            const existingAnswerIndex = answers.findIndex(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (item: any) => item.type === questionAnswerType.OTHER_ANSWER
            );
            if (existingAnswerIndex === -1) return;

            const newAnswers = [...answers];
            newAnswers[existingAnswerIndex].answer = text;
            setValue('data.answers', newAnswers);
        }
        // eslint-disable-next-line
    }, [anotherAnswerText, addAnotherAnswer]);

    useEffect(() => {
        if (multipleAnswers) {
            const trimmed = multipleAnswers
                ?.split('\n')
                ?.map((line: string) => line?.slice(0, 255))
                ?.join('\n');
            if (trimmed !== multipleAnswers) {
                setValue('data.multipleAnswers', trimmed);
            }
        }
    }, [multipleAnswers]);

    return (
        <Box>
            <Stack p={'0px 35px 35px 35px'} gap={1}>
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                    <QuestionDnD {...props} question={question} />
                    {!question.answerFromOtherQuestionLogic?.id &&
                        answers?.length === 0 && (
                            <Box
                                display={'flex'}
                                alignItems={'center'}
                                width={'100%'}
                            >
                                <IconButton onClick={() => insertAnswer(0)}>
                                    <PlusIcon />
                                </IconButton>
                                <Box component={'span'}>Thêm đáp án</Box>
                            </Box>
                        )}
                    {!insertMultipleAnswers && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                width: '100%',
                            }}
                        >
                            {answers?.map(
                                (item: IQuestionAnswer, fieldIndex: number) => {
                                    const answerValue = watch(
                                        `data.answers.${fieldIndex}.answer`
                                    );
                                    const hasError =
                                        errors?.answers?.[fieldIndex]?.answer;
                                    return (
                                        <Grid
                                            container
                                            key={`${item?.id}_${fieldIndex}`}
                                            spacing={1}
                                            alignItems={'center'}
                                            justifyContent={'flex-start'}
                                        >
                                            <Grid item xs="auto">
                                                <FormControlLabel
                                                    value=""
                                                    control={
                                                        <Radio
                                                            checked={
                                                                fieldIndex === 0
                                                            }
                                                        />
                                                    }
                                                    label=""
                                                />
                                            </Grid>
                                            <Grid item xs>
                                                {editingIndex === fieldIndex ||
                                                hasError ? (
                                                    <BaseTextField
                                                        autoComplete="off"
                                                        fullWidth
                                                        name={`data.answers.${fieldIndex}.answer`}
                                                        size="small"
                                                        placeholder="Nhập tùy chọn câu trả lời"
                                                        sx={styles.answer}
                                                        onBlur={() =>
                                                            handleSave(
                                                                fieldIndex
                                                            )
                                                        }
                                                        onFocus={() =>
                                                            focusOnField(
                                                                fieldIndex
                                                            )
                                                        }
                                                        InputProps={{
                                                            inputProps: {
                                                                maxLength: 255,
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => {
                                                            if (
                                                                item?.type ===
                                                                questionAnswerType.NORMAL_ANSWER
                                                            ) {
                                                                focusOnField(
                                                                    fieldIndex
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        {answerValue}
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid item xs={'auto'}>
                                                {watch('data.insertImage') && (
                                                    <Box
                                                        position={'relative'}
                                                        display={'flex'}
                                                        flexDirection={'column'}
                                                        alignItems={'flex-end'}
                                                    >
                                                        <BaseImageUpload
                                                            name={`data.answers.${fieldIndex}.image`}
                                                            sx={{
                                                                maxWidth:
                                                                    '41px',
                                                                height: '41px',
                                                                backgroundColor:
                                                                    'unset',
                                                            }}
                                                        />
                                                        {watch(
                                                            `data.answers.${fieldIndex}.image`
                                                        ) && (
                                                            <Box
                                                                position={
                                                                    'absolute'
                                                                }
                                                                right={-15}
                                                                top={-15}
                                                            >
                                                                <IconButton
                                                                    onClick={() =>
                                                                        removeAnswerImage(
                                                                            fieldIndex
                                                                        )
                                                                    }
                                                                >
                                                                    <CloseIcon
                                                                        sx={{
                                                                            fontSize:
                                                                                '16px',
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Grid>
                                            <Grid item xs={1}>
                                                {item.type ===
                                                    questionAnswerType.NORMAL_ANSWER && (
                                                    <Box display={'flex'}>
                                                        {answers.length > 1 && (
                                                            <IconButton
                                                                onClick={() =>
                                                                    checkRemoveAnswer(
                                                                        fieldIndex
                                                                    )
                                                                }
                                                            >
                                                                <MinusIcon />
                                                            </IconButton>
                                                        )}
                                                        <IconButton
                                                            onClick={() =>
                                                                insertAnswer(
                                                                    fieldIndex
                                                                )
                                                            }
                                                        >
                                                            <PlusIcon />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Grid>
                                        </Grid>
                                    );
                                }
                            )}
                        </Box>
                    )}
                    {insertMultipleAnswers && (
                        <BaseTextField
                            multiline
                            name={`data.multipleAnswers`}
                            fullWidth
                            size="small"
                            minRows={4}
                            placeholder="Chèn nhiều đáp án cùng lúc"
                            sx={styles.multipleAnswer}
                            onBlur={handleTextareaBlur}
                        />
                    )}
                </Box>
            </Stack>

            <Stack
                display={
                    currentCampaign?.kind === 'public_with_code'
                        ? 'none'
                        : 'flex'
                }
                flexDirection={'row'}
                flexWrap={'wrap'}
                gap={4}
                p={'35px'}
                sx={{
                    borderTop: `2px dashed var(--color-primary)`,
                }}
            >
                <Stack flexDirection={'column'} gap={'20px'} display={'none'}>
                    <RequiredLabel
                        label="Số hàng"
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseNumberField
                        sx={styleInputNumber}
                        size="small"
                        name={`data.numberOfRows`}
                        min={1}
                        max={20}
                        InputProps={{
                            readOnly: true,
                        }}
                        forceDisableMinus={
                            !!question.answerFromOtherQuestionLogic?.id
                        }
                        forceDisablePlus={
                            !!question.answerFromOtherQuestionLogic?.id
                        }
                        disabled={!!question.answerFromOtherQuestionLogic?.id}
                        showMinus={false}
                        showPlus={false}
                    />
                </Stack>

                <Stack gap={'10px'}>
                    <RequiredLabel
                        label={`Thêm "Đáp án khác"`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <Stack flexDirection={'row'} alignItems={'center'}>
                        <BaseCheckbox
                            name={`data.addAnotherAnswer`}
                            label={``}
                            onClick={(e) =>
                                handleAddAnotherAnswer(
                                    (e.target as HTMLInputElement).checked
                                )
                            }
                        />
                        <BaseTextField
                            name="data.anotherAnswerText"
                            fullWidth
                            size="small"
                            placeholder="Khác (Vui lòng ghi rõ)"
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: common.white,
                                    height: '24px',
                                    fieldset: {
                                        border: `1px solid ${common.black}`,
                                    },
                                },
                            }}
                            InputProps={{
                                inputProps: {
                                    maxLength: 255,
                                },
                            }}
                        />
                    </Stack>
                </Stack>
                <Stack gap={'10px'}>
                    <RequiredLabel
                        label={`Thêm "Không có đáp án nào trên đây"`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseCheckbox
                        name={`data.addNoAnswersYet`}
                        onClick={(e) =>
                            handleAddNoAnswerYet(
                                (e.target as HTMLInputElement).checked
                            )
                        }
                        label={``}
                    />
                </Stack>
                <Stack gap={'10px'}>
                    <RequiredLabel
                        label={`Chèn nhiều đáp án cùng lúc`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseCheckbox
                        name={`data.insertMultipleAnswers`}
                        label={``}
                    />
                </Stack>
                <Stack gap={'20px'}>
                    <RequiredLabel
                        label={`Sắp xếp các đáp án`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseSelectField
                        name={`data.sortAnswers`}
                        size="small"
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '24px',
                                backgroundColor: common.white,
                            },
                        }}
                        defaultValue={'default'}
                    >
                        <MenuItem value={'default'}>Mặc định</MenuItem>
                        <MenuItem value={'AZ'}>A-Z</MenuItem>
                        <MenuItem value={'ZA'}>Z-A</MenuItem>
                        <MenuItem value={'random'}>Ngẫu nhiên</MenuItem>
                    </BaseSelectField>
                </Stack>
                <Stack gap={'10px'}>
                    <RequiredLabel
                        label={`Chèn ảnh ở đáp án`}
                        required={false}
                        sx={{ fontSize: '16px', fontWeight: '500' }}
                    />
                    <BaseCheckbox name={`data.insertImage`} label={``} />
                </Stack>
            </Stack>
            {openModalDeleteAnswer && (
                <ModalConfirm
                    open={openModalDeleteAnswer}
                    onClose={() => setOpenModalDeleteAnswer(false)}
                    title={'Xác nhận xóa đáp án'}
                    textOk="Xóa"
                    onOk={() => {
                        removeAnswer(modalDeleteAnswerIndex);
                        setOpenModalDeleteAnswer(false);
                        setModalDeleteAnswerText('');
                        setModalDeleteAnswerIndex(0);
                    }}
                    content={modalDeleteAnswerText}
                />
            )}
        </Box>
    );
};
const styles = createStyleSheet({
    answer: {
        ...styleInput,
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            padding: '13px 29px 13px 29px',
            height: '44px',
            fontSize: '17px',
        },
    },
    multipleAnswer: {
        ...styleInput,
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            padding: '13px 29px 13px 29px',
            height: 'auto',
            fontSize: '17px',
            borderRadius: '5px',
        },
    },
});

export default QuestionSingleChoice;
